import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

import idbStorage from './idbStorage';

import loaderReducer from './loader';
import userReducer from './user';
import appReducer from './app';
import onboardingReducer from './onboarding';
import cacheReducer from './cache';
import workerDataReducer from './workerData';
import { indexedDBSyncMiddleware } from './middleware';
import { logoutUser } from './user';
import { setSessionExpiredHandler } from 'utils/sessionExpiry';
import { ROUTES } from 'constants/routes';

const persistConfig = {
  key: 'root',
  version: 1,
  storage: idbStorage,
  blacklist: ['loader'],
  // transforms: [appWhitelistTransform],
};

const rootReducer = combineReducers({
  app: appReducer,
  user: userReducer,
  onboarding: onboardingReducer,
  loader: loaderReducer,
  cache: cacheReducer,
  workerData: workerDataReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  devTools:
    process.env.NODE_ENV !== 'production'
      ? {
          name: 'worker-portal',
        }
      : false,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(indexedDBSyncMiddleware),
});

//* Runs once, on the first 401 while someone is signed in (see utils/sessionExpiry). The reload
//* discards the requests the API clients left pending, and AppRoot shows the toast from the flag.
setSessionExpiredHandler(() => {
  if (!store.getState().user?.userId) return false;
  store.dispatch(logoutUser({ sessionExpired: true })).finally(() => {
    localStorage.setItem('sessionExpired', 'true');
    window.location.replace(ROUTES.LOGIN.path);
  });
  return true;
});

let persistor = persistStore(store);

export { persistor };
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
